/**
 * TDM Filling Option v5
 * =====================
 * Mimics Horizon's exact cart add behavior for seamless integration.
 * 
 * Key: Uses the same approach as product-form.js:
 * 1. Collects section IDs from cart-items-component
 * 2. Sends FormData to /cart/add.js
 * 3. Dispatches CartAddEvent with response sections
 * 
 * Place in: assets/tdm-filling-option.js
 */

(function() {
  'use strict';

  const TDMFilling = {
    config: {
      fillingProductId: 15627277402437,
      selectors: {
        container: '.tdm-filling-option',
        checkbox: '.tdm-filling-checkbox',
        confirmation: '.tdm-filling-confirmation',
        form: 'form[data-type="add-to-cart-form"]',
        productFormComponent: 'product-form-component',
        quantityInput: '.quantity-selector input[type="number"], input[name="quantity"]',
        addToCartComponent: 'add-to-cart-component',
        addButton: '[ref="addToCartButton"]',
        stickyButton: '.sticky-add-to-cart__button',
        infoBtn: '.tdm-filling-info-btn',
        tooltip: '.tdm-filling-tooltip',
        cartItemsComponent: 'cart-items-component'
      }
    },

    state: {
      fillingVariantId: null,
      isSubmitting: false,
      boundContainers: new WeakSet(),
      tooltipBound: false
    },

    /**
     * Initialize
     */
    async init() {
      const container = document.querySelector(this.config.selectors.container);
      if (!container) return;

      const datasetVariantId = container.dataset.fillingVariantId;
      if (datasetVariantId) {
        this.state.fillingVariantId = datasetVariantId;
      }
      
      if (!this.state.fillingVariantId) {
        await this.fetchFillingVariant();
      }

      if (!this.state.fillingVariantId) {
        console.warn('[TDM Filling] No variant ID found');
      }

      this.bindEvents(container);
      if (this.state.fillingVariantId) {
        console.log('[TDM Filling v5] Initialized', { variantId: this.state.fillingVariantId });
      }
    },

    /**
     * Fetch variant ID
     */
    async fetchFillingVariant() {
      const handles = [
        'interieur-de-coussin-10',
        'interieur-de-coussin-10-e',
        'garnissage-coussin',
        'interieur-coussin'
      ];

      for (const handle of handles) {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            const product = await res.json();
            if (product.variants?.[0]?.id) {
              this.state.fillingVariantId = String(product.variants[0].id);
              return;
            }
          }
        } catch (e) { continue; }
      }
    },

    /**
     * Bind events
     */
    bindEvents(container) {
      if (this.state.boundContainers.has(container)) return;
      this.state.boundContainers.add(container);

      // Checkbox UI
      const checkbox = container.querySelector(this.config.selectors.checkbox);
      if (checkbox) {
        checkbox.addEventListener('change', (e) => this.onCheckboxChange(e, container));
      }

      // Tooltip
      this.bindTooltip(container);
    },

    /**
     * Bind tooltip
     */
    bindTooltip(container) {
      const infoBtn = container.querySelector(this.config.selectors.infoBtn);
      if (!infoBtn) return;

      infoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        infoBtn.querySelector(this.config.selectors.tooltip)?.classList.toggle('is-visible');
      });

      if (!this.state.tooltipBound) {
        document.addEventListener('click', (e) => {
          if (!e.target.closest(this.config.selectors.infoBtn)) {
            document.querySelectorAll(`${this.config.selectors.tooltip}.is-visible`)
              .forEach(t => t.classList.remove('is-visible'));
          }
        });
        this.state.tooltipBound = true;
      }
    },

    /**
     * Should add filling?
     */
    shouldAddFilling(container = document) {
      const checkbox = container.querySelector(this.config.selectors.checkbox);
      return !!checkbox?.checked && !checkbox.disabled;
    },

    /**
     * Get quantity
     */
    getQuantity(productFormComponent = null) {
      const scope = productFormComponent || document;
      const input = scope.querySelector(this.config.selectors.quantityInput);
      return parseInt(input?.value, 10) || 1;
    },

    /**
     * Get main variant ID
     */
    getMainVariantId(productFormComponent = null) {
      const scope = productFormComponent || document;
      return scope.querySelector('input[name="id"]')?.value;
    },

    /**
     * Get filling container for a specific product form component
     */
    getContainerForForm(productFormComponent = null) {
      if (productFormComponent) {
        return productFormComponent.querySelector(this.config.selectors.container);
      }
      return document.querySelector(this.config.selectors.container);
    },

    /**
     * Get add to cart form for a specific product form component
     */
    getForm(productFormComponent = null) {
      if (productFormComponent) {
        return productFormComponent.querySelector(this.config.selectors.form);
      }
      return document.querySelector(this.config.selectors.form);
    },

    /**
     * Check if filling should be added for a specific product form component
     */
    shouldAddFillingForForm(productFormComponent = null) {
      const container = this.getContainerForForm(productFormComponent);
      if (!container) return false;
      return this.shouldAddFilling(container);
    },

    /**
     * Ensure filling variant ID is available
     */
    async ensureFillingVariantId(container = null) {
      if (this.state.fillingVariantId) return this.state.fillingVariantId;

      const datasetVariantId = container?.dataset?.fillingVariantId;
      if (datasetVariantId) {
        this.state.fillingVariantId = datasetVariantId;
        return this.state.fillingVariantId;
      }

      await this.fetchFillingVariant();
      return this.state.fillingVariantId;
    },

    /**
     * Handle submit from ProductFormComponent
     */
    handleProductFormSubmit(productFormComponent = null, event = null) {
      if (!this.shouldAddFillingForForm(productFormComponent)) return false;

      if (event) {
        event.tdmFillingHandled = true;
      }

      const container = this.getContainerForForm(productFormComponent);
      this.addBothToCart(productFormComponent, container);
      return true;
    },

    /**
     * Handle form submit
     */
    onFormSubmit(e) {
      const productFormComponent = e.target?.closest?.(this.config.selectors.productFormComponent);
      if (!this.shouldAddFillingForForm(productFormComponent)) return;
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      this.addBothToCart(productFormComponent, this.getContainerForForm(productFormComponent));
    },

    /**
     * Handle sticky button
     */
    onStickyClick(e) {
      const productFormComponent = document.querySelector(this.config.selectors.productFormComponent);
      if (!this.shouldAddFillingForForm(productFormComponent)) return;
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      this.addBothToCart(productFormComponent, this.getContainerForForm(productFormComponent));
    },

    /**
     * Add both products - mimics Horizon's product-form.js exactly
     */
    async addBothToCart(productFormComponent = null, container = null) {
      if (this.state.isSubmitting) return;
      this.state.isSubmitting = true;

      const resolvedProductFormComponent =
        productFormComponent || document.querySelector(this.config.selectors.productFormComponent);
      const resolvedContainer = container || this.getContainerForForm(resolvedProductFormComponent);
      const mainVariantId = this.getMainVariantId(resolvedProductFormComponent);
      const quantity = this.getQuantity(resolvedProductFormComponent);
      const form = this.getForm(resolvedProductFormComponent);

      if (!mainVariantId || !form) {
        this.state.isSubmitting = false;
        return;
      }

      const fillingVariantId = await this.ensureFillingVariantId(resolvedContainer);
      if (!fillingVariantId) {
        this.state.isSubmitting = false;
        this.showError('Filling variant unavailable.');
        return;
      }

      // Trigger button animation like Horizon does
      this.animateButton(resolvedProductFormComponent);

      try {
        // Step 1: Collect section IDs (exactly like Horizon)
        const cartItemsComponents = document.querySelectorAll(this.config.selectors.cartItemsComponent);
        const sectionIds = [];
        cartItemsComponents.forEach((item) => {
          if (item.dataset?.sectionId) {
            sectionIds.push(item.dataset.sectionId);
          }
        });

        // Step 2: Build FormData for FIRST product (main cushion)
        const formData1 = new FormData(form);
        formData1.set('id', mainVariantId);
        formData1.set('quantity', quantity.toString());
        formData1.delete('add_filling');
        if (sectionIds.length) {
          formData1.append('sections', sectionIds.join(','));
        }

        // Step 3: Add main product
        const response1 = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          body: formData1,
          headers: { 'Accept': 'text/html' }
        });

        const result1 = await response1.json();
        
        if (result1.status) {
          throw new Error(result1.description || result1.message);
        }

        // Step 4: Build FormData for SECOND product (filling)
        const formData2 = new FormData();
        formData2.append('id', fillingVariantId);
        formData2.append('quantity', quantity.toString());
        if (sectionIds.length) {
          formData2.append('sections', sectionIds.join(','));
        }

        // Step 5: Add filling product
        const response2 = await fetch(Theme.routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          body: formData2,
          headers: { 'Accept': 'text/html' }
        });

        const result2 = await response2.json();

        if (result2.status) {
          throw new Error(result2.description || result2.message);
        }

        console.log('[TDM Filling] Both products added');

        // Step 6: Dispatch CartAddEvent like Horizon does
        // This is what triggers the cart drawer to update!
        if (resolvedProductFormComponent) {
          const CartAddEvent = this.getCartAddEventClass();
          if (CartAddEvent) {
            resolvedProductFormComponent.dispatchEvent(
              new CartAddEvent({}, mainVariantId, {
                source: 'tdm-filling',
                itemCount: quantity * 2, // Both items
                productId: resolvedProductFormComponent.dataset?.productId,
                sections: result2.sections // Sections from last response
              })
            );
          } else {
            // Fallback: dispatch custom event
            this.dispatchFallbackEvent(result2);
          }
        } else {
          this.dispatchFallbackEvent(result2);
        }

      } catch (error) {
        console.error('[TDM Filling] Error:', error);
        this.showError(error.message);
      } finally {
        this.state.isSubmitting = false;
      }
    },

    /**
     * Get CartAddEvent class from Horizon's module system
     */
    getCartAddEventClass() {
      // CartAddEvent might be available globally or we need to create compatible event
      if (window.CartAddEvent) {
        return window.CartAddEvent;
      }

      // Create a compatible CartAddEvent class
      // IMPORTANT: Must use 'cart:update' to match Horizon's ThemeEvents.cartUpdate
      return class CartAddEvent extends CustomEvent {
        static eventName = 'cart:update';

        constructor(resource, sourceId, data) {
          super('cart:update', {
            bubbles: true,
            composed: true,
            detail: { resource, sourceId, data }
          });
        }
      };
    },

    /**
     * Dispatch fallback events if CartAddEvent isn't available
     */
    dispatchFallbackEvent(result) {
      // Dispatch the event that cart-drawer listens to
      // IMPORTANT: Use 'cart:update' to match Horizon's ThemeEvents.cartUpdate
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: result,
          sourceId: 'tdm-filling',
          data: {
            source: 'tdm-filling',
            sections: result.sections
          }
        }
      }));

      // Also update sections manually as backup
      if (result.sections) {
        this.updateSections(result.sections);
      }

      // Open the cart drawer
      this.openCartDrawer();
    },

    /**
     * Update sections in DOM
     */
    updateSections(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        const selectors = [
          `#shopify-section-${id}`,
          `[data-section-id="${id}"]`
        ];
        
        for (const selector of selectors) {
          const el = document.querySelector(selector);
          if (el) {
            el.innerHTML = html;
            break;
          }
        }
      });
    },

    /**
     * Open cart drawer
     */
    openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else if (typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
        }
      }
    },

    /**
     * Animate the add to cart button
     */
    animateButton(productFormComponent = null) {
      const scope = productFormComponent || document;
      const addToCartComponent = scope.querySelector(this.config.selectors.addToCartComponent);
      const button = scope.querySelector(this.config.selectors.addButton);
      
      if (button) {
        button.dataset.added = 'true';
        setTimeout(() => {
          button.removeAttribute('data-added');
        }, 2500);
      }

      // Also trigger the component's animation if available
      if (addToCartComponent && typeof addToCartComponent.animateAddToCart === 'function') {
        addToCartComponent.animateAddToCart();
      }
    },

    /**
     * Checkbox UI change
     */
    onCheckboxChange(e, container) {
      container.classList.toggle('is-checked', e.target.checked);
      const confirmation = container.querySelector(this.config.selectors.confirmation);
      if (confirmation) {
        confirmation.style.display = e.target.checked ? 'flex' : 'none';
      }
    },

    /**
     * Show error toast
     */
    showError(message) {
      document.querySelector('.tdm-filling-error')?.remove();

      const toast = document.createElement('div');
      toast.className = 'tdm-filling-error';
      toast.setAttribute('role', 'alert');
      toast.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span>${message}</span>
      `;

      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 5000);
    }
  };

  // Init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TDMFilling.init());
  } else {
    TDMFilling.init();
  }

  document.addEventListener('shopify:section:load', () => TDMFilling.init());

  window.TDMFilling = TDMFilling;
})();
